<template>
	<header class="fixed top-0 z-40 w-full bg-white transition-shadow duration-1000 ease-in-out" :class="dropshadow">
		<ResponsiveContainer class="flex items-center justify-between h-[75px] px-2">
			<NuxtLink class="md:max-w-[235px] mx-2" :to="`/${$i18n.locale}`">
				<DjLogo v-if="region != ''" :region="region" />
			</NuxtLink>
			<div class="hidden md:flex items-center justify-between">
				<nav class="flex items-center justify-between">
					<BaseLink class="self-start text-sm mx-7" @click="scrollTo('seb')" type="black">
						{{ $t("workshop.petyaWorkshop.nav.seb") }}
					</BaseLink>
					<BaseLink class="self-start text-sm mx-7" @click="scrollTo('why')" type="black">
						{{ $t("workshop.petyaWorkshop.nav.what") }}
					</BaseLink>
					<BaseLink class="self-start text-sm mx-7" @click="scrollTo('why')" type="black">
						{{ $t("workshop.petyaWorkshop.nav.why") }}
					</BaseLink>
				</nav>
			</div>
			<div class="flex flex-1 md:flex-none justify-end items-center">
				<BaseLink class="w-full mb-3" :href="`${dashboardUrl}/${$i18n.locale}/login`" target="_blank">
					<BaseButton type="tertiary" class="hidden md:flex capitalize ml-6" :subtitle="$t('forEmployers')">
						{{ $t("loginBtn") }}
					</BaseButton>
				</BaseLink>
				<BaseLink class="w-full mb-3" :href="`${dashboardUrl}/${$i18n.locale}/register`" target="_blank">
					<BaseButton
						type="primary-red"
						class="hidden md:flex capitalize ml-4"
						:subtitle="$t('forEmployers')"
					>
						{{ $t("registerBtn") }}
					</BaseButton>
				</BaseLink>
			</div>
			<div class="md:hidden p-3">
				<BaseLink @click="showMobileMenuMain = true" type="grey" class="">
					<span class="material-icons-outlined">menu</span>
				</BaseLink>
			</div>
			<BaseMobileNav :openItem="showMobileMenuMain" @closeAll="showMobileMenuMain = false" class="md:hidden">
				<ul>
					<li class="flex w-full justify-start pt-1 pb-8 pl-1">
						<DjLogo v-if="region != ''" :region="region" class="w-auto" />
					</li>
					<li class="flex justify-between w-full py-4">
						<BaseLink class="font-medium text-2xl" @click="scrollTo('seb')" type="black">
							{{ $t("workshop.petyaWorkshop.nav.seb") }}
						</BaseLink>
					</li>
					<li class="flex justify-between w-full py-4">
						<BaseLink class="font-medium text-2xl" @click="scrollTo('what')" type="black">
							{{ $t("workshop.petyaWorkshop.nav.about") }}
						</BaseLink>
					</li>
					<li class="flex justify-between w-full py-4">
						<BaseLink class="font-medium text-2xl" @click="scrollTo('why')" type="black">
							{{ $t("workshop.petyaWorkshop.nav.reg") }}
						</BaseLink>
					</li>
					<li class="flex justify-between w-full py-4">
						<BaseLink class="w-full mb-3" :href="`${dashboardUrl}/${$i18n.locale}/login`" target="_blank">
							<BaseButton type="tertiary" class="flex capitalize ml-6" :subtitle="$t('forEmployers')">
								{{ $t("loginBtn") }}
							</BaseButton>
						</BaseLink>
						<BaseLink
							class="w-full mb-3"
							:href="`${dashboardUrl}/${$i18n.locale}/register`"
							target="_blank"
						>
							<BaseButton type="primary-red" class="flex capitalize ml-4" :subtitle="$t('forEmployers')">
								{{ $t("registerBtn") }}
							</BaseButton>
						</BaseLink>
					</li>
				</ul>
			</BaseMobileNav>
		</ResponsiveContainer>
	</header>
</template>

<script lang="ts">
import { defineComponent, ref, nextTick } from "vue";
import { useI18n } from "vue-i18n";

export default defineComponent({
	name: "StickyHeader",
	setup() {
		const { locale } = useI18n();

		const router = useRouter();
		const route = useRoute();
		const isAuth = useState("isAuth");

		const region = ref("");

		const dropshadow = ref("");
		const showEmployersDropdown = ref(false);
		const showMobileMenuMain = ref(false);
		const showMobileMenuForEmployers = ref(false);
		const showMobileMenuProfile = ref(false);
		const showMobileMenuAccount = ref(false);

		const dashboardUrl = ref("");

		watch(router.currentRoute, () => {
			// Hide mobile navigation on url change
			hideMenus();
		});

		function hideMenus() {
			showEmployersDropdown.value = false;
			showMobileMenuMain.value = false;
			showMobileMenuForEmployers.value = false;
			showMobileMenuProfile.value = false;
			showMobileMenuAccount.value = false;
		}

		function handleScroll() {
			let scrollPosition = window.scrollY;
			scrollPosition > 0 ? (dropshadow.value = "drop-shadow-sm") : (dropshadow.value = "drop-shadow-none");
		}

		const locales = ref<string[]>();

		function logout() {
			useAuth.logout();
		}

		function getLocalizedRouteName(targetLocale: string): string | symbol | undefined {
			if (typeof route.name !== "string") {
				return route.name ?? undefined;
			}

			if (route.name.includes("___")) {
				return route.name.replace(/___[a-z]{2}$/i, `___${targetLocale}`);
			}

			return route.name;
		}

		function resolveLocalizedJobPath(targetLocale: string): string | null {
			const routeName = String(route.name ?? "");
			if (!routeName.includes("job-company_slug-slug")) {
				return null;
			}

			const currentSlug = Array.isArray(route.params.slug) ? route.params.slug[0] : route.params.slug;
			if (!currentSlug) {
				return null;
			}

			const payloadData = useNuxtApp().payload.data as Record<string, unknown>;
			let localizedSlug = "";

			for (const value of Object.values(payloadData)) {
				if (!value || typeof value !== "object") {
					continue;
				}

				const item = value as { slug?: Record<string, string> };
				if (item.slug && Object.values(item.slug).includes(currentSlug)) {
					localizedSlug = item.slug[targetLocale] || "";
					break;
				}
			}

			if (!localizedSlug) {
				return null;
			}

			const localizedRouteName = getLocalizedRouteName(targetLocale);
			if (!localizedRouteName) {
				return null;
			}

			const resolvedRoute = router.resolve({
				name: localizedRouteName,
				params: {
					...route.params,
					slug: localizedSlug,
				},
				query: route.query,
				hash: route.hash,
			});

			return resolvedRoute.fullPath;
		}

		function handleLocaleChange() {
			const targetLocale = locale.value;

			// 1. Get the actual current locale straight from the URL before navigation
			let currentUrlLocale = route.path.split("/").filter(Boolean)[0];
			if (typeof route.name === "string") {
				const match = route.name.match(/___([a-z]{2})$/i);
				if (match) {
					currentUrlLocale = match[1];
				}
			}

			if (String(route.name ?? "").includes("job-company_slug-slug")) {
				const localizedJobPath = resolveLocalizedJobPath(targetLocale);
				if (!localizedJobPath) {
					// 2. Safely revert the locale back if no localized path exists
					nextTick(() => {
						locale.value = currentUrlLocale;
					});
					return;
				}
				router.push(localizedJobPath).then(() => {
					if (useAuth.getUser()) {
						useAuth.updateUser(); // Update user data after changing locale
					}
				});
				return;
			}

			const localizedRouteName = getLocalizedRouteName(targetLocale);
			const localizedResolvedRoute =
				localizedRouteName == null
					? null
					: router.resolve({
							name: localizedRouteName,
							params: route.params,
							query: route.query,
							hash: route.hash,
					  });
			const nextPath = localizedResolvedRoute?.fullPath || route.fullPath;

			router.push(nextPath).then(() => {
				// Keep the user data update consistent here too
				if (useAuth.getUser()) {
					useAuth.updateUser();
				}
			});
		}

		function scrollTo(elementId) {
			const el = document.getElementById(elementId).offsetTop;
			window.scrollTo({ top: el - 80, left: 0, behavior: "smooth" });
			showMobileMenuMain.value = false;
		}

		onMounted(() => {
			window.addEventListener("scroll", handleScroll);
			locales.value = useRegion.getCurrentRegion().availableLangs;
			region.value = useRegion.getCurrentRegion().code;
			dashboardUrl.value = `https://dashboard.dreamjobs.${region.value.toLowerCase()}`;
		});

		return {
			locale,
			isAuth,
			dropshadow,
			hideMenus,
			logout,
			showEmployersDropdown,
			showMobileMenuMain,
			showMobileMenuForEmployers,
			showMobileMenuProfile,
			showMobileMenuAccount,
			locales,
			handleLocaleChange,
			region,
			dashboardUrl,
			scrollTo,
		};
	},
});
</script>
